import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { Purchase } from 'api/purchases'
import HeaderCell from 'components/HeaderCell'
import Modal from 'components/ModalContainer'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { OrderDetails, ProviderPurchase } from 'models/Order'
import moment from 'moment'
import BodyCellContainer from 'scenes/Reports/components/Table/BodyCellContainer'
import { merchantTheme } from 'theme'
import { currencyFormatter } from 'utilities/formatters'
import OrderReceipt from './OrderReceipt'

const t = buildTranslate('users.view.activity.order_details')

type PropsT = {
  show?: boolean
  order: OrderDetails | Purchase
  onHide: () => void
}

export const SCROLLABLE_DIALOG_STYLE = {
  maxHeight: 'calc(100vh - 48px)',
  overflowY: 'auto',
}

function formatDate(date: string) {
  return {
    day: moment(date).format('MMM D, YYYY'),
    time: moment(date).format('h:mm a'),
  }
}

// A purchase timestamp is card settlement; a POS `placed_at` can be order open.
function earliestDate(purchasedAt: string, placedAt?: string | null): string {
  if (!placedAt) return purchasedAt
  return moment(placedAt).isBefore(moment(purchasedAt)) ? placedAt : purchasedAt
}

const distinctValues = (values: string[]) => Array.from(new Set(values))
const onlyValue = (values: string[]) => (values.length === 1 ? values[0] : null)
const orderUids = (purchases: ProviderPurchase[]) =>
  distinctValues(
    purchases.flatMap(p =>
      p.order_uid && p.order_uid !== p.uid ? [p.order_uid] : []
    )
  )

// An Olo basket can carry two order numbers; the settlement's is the placed order.
function externalOrderIdFor(purchases: ProviderPurchase[]): string | null {
  const uid = onlyValue(distinctValues(purchases.map(p => p.uid)))
  if (!uid) return null

  const settlementOrderUid = onlyValue(
    orderUids(purchases.filter(p => p.type === 'settlement'))
  )
  const sharedOrderUid = onlyValue(orderUids(purchases))
  return settlementOrderUid ?? sharedOrderUid ?? uid
}

const OrderDetailsModal = ({ show = true, order, onHide }: PropsT) => {
  const [css] = useStyletron()
  const { colors } = merchantTheme
  const purchaseId = order.id

  const posOrderEnabled = useFlag('purchase-linked-pos-order-mx', false)
  const posOrder = posOrderEnabled ? order.pos_order : null
  const showReceipt = !!posOrder?.items?.length

  const orderDate = formatDate(
    earliestDate(order.purchased_at, posOrder?.placed_at)
  )

  const renderItems = () => {
    if (showReceipt && posOrder) {
      return (
        <OrderReceipt
          posOrder={posOrder}
          purchaseTotalCents={Math.round(order.prices.total * 100)}
        />
      )
    }

    if (posOrderEnabled && !order.items.length) {
      return (
        <Text.BodyText4 tag="div" color={colors.grey70} className="mt-m">
          {t('no_pos_data')}
        </Text.BodyText4>
      )
    }

    return (
      <Switch condition={!!order.items.length}>
        <Text.Header4 bold tag="div" className="mt-l mb-s">
          {t('items_in_this_order')}
        </Text.Header4>
        <table className="full-width">
          <thead>
            <tr>
              <HeaderCell>{t('item')}</HeaderCell>
              <HeaderCell />
            </tr>
          </thead>
          <tbody>
            {order.items.map((item, index) => (
              <tr key={`order-item-${index}`}>
                <BodyCellContainer isErrored={false} isLoading={false}>
                  <Text.BodyText4 color={colors.grey70}>
                    {item.label}
                  </Text.BodyText4>
                </BodyCellContainer>
                <BodyCellContainer isErrored={false} isLoading={false}>
                  <Text.BodyText4 color={colors.grey70} />
                </BodyCellContainer>
              </tr>
            ))}
          </tbody>
        </table>
      </Switch>
    )
  }

  // If we have loyalty provider purchases, we need to expose that too in order for t
  // the merchant to be able to use it in their POS dashboard.
  // We exclude card/anon etc, because this only applies to loyalty purchases.
  const loyaltyProviderPurchases = (order.provider_purchases ?? []).filter(p =>
    p.provider.toLowerCase().includes('loyalty')
  )

  const checkInProviderPurchases = (order.provider_purchases ?? []).filter(
    p => p.type === 'check_in'
  )

  const externalOrderId = externalOrderIdFor([
    ...loyaltyProviderPurchases,
    ...checkInProviderPurchases,
  ])

  return (
    <Modal
      isOpen={show}
      title={`${t('purchase')} ${purchaseId}`}
      subtitle={
        externalOrderId
          ? `${t('external_order_id')}: ${externalOrderId}`
          : undefined
      }
      proceedText={t('okay')}
      className={`fs-unmask ${css({
        overflowWrap: 'anywhere',
      })}`}
      hideCancel
      onClose={onHide}
      onProceed={onHide}
      dialogStyle={showReceipt ? SCROLLABLE_DIALOG_STYLE : undefined}
      zIndex={3000}
    >
      <div>
        <div className="mt-m">
          {/* Order details section */}
          {/* Purchases can be imported from receipts without a location */}
          {!!order.location && (
            <>
              <Text.BodyText4 className="mb-xs" tag="div" bold>
                {t('location')}
              </Text.BodyText4>
              {[
                order.location.name,
                order.location.street,
                `${order.location.city}, ${order.location.state} ${order.location.zip}`,
              ].map(locationItem => (
                <Text.BodyText4
                  key={locationItem}
                  tag="div"
                  color={colors.grey70}
                >
                  {locationItem}
                </Text.BodyText4>
              ))}
            </>
          )}

          <Text.BodyText4 className="mb-xs mt-m" tag="div" bold>
            {t('date_and_time')}
          </Text.BodyText4>
          {[orderDate.day, orderDate.time].map(dateItem => (
            <Text.BodyText4 key={dateItem} tag="div" color={colors.grey70}>
              {dateItem}
            </Text.BodyText4>
          ))}

          <Switch condition={showReceipt}>
            <Text.BodyText4 className="mb-xs mt-m" tag="div" bold>
              {t('card_processed_for')}
            </Text.BodyText4>
            <Text.BodyText4 tag="div" color={colors.grey70}>
              {currencyFormatter(order.prices.total)}
            </Text.BodyText4>
          </Switch>

          <Switch condition={!showReceipt}>
            <Text.BodyText4 className="mb-xs mt-m" tag="div" bold>
              {t('order_details')}
            </Text.BodyText4>
            {[`${t('total')}: ${currencyFormatter(order.prices.total)}`].map(
              orderItem => (
                <Text.BodyText4 key={orderItem} tag="div" color={colors.grey70}>
                  {orderItem}
                </Text.BodyText4>
              )
            )}
          </Switch>

          {/* Items section */}
          {renderItems()}
        </div>
      </div>
    </Modal>
  )
}

export default OrderDetailsModal
